import React from 'react';
import { BetaSignup } from '../../types';

interface SignupsTableProps {
  signups: BetaSignup[];
  selectedSignups: string[];
  onStatusUpdate: (signupId: string, status: string) => void;
  onSelectionChange: (signupId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
}

const SignupsTable: React.FC<SignupsTableProps> = ({
  signups,
  selectedSignups,
  onStatusUpdate,
  onSelectionChange,
  onSelectAll
}) => {
  const allSelected = signups.length > 0 && selectedSignups.length === signups.length;
  const someSelected = selectedSignups.length > 0 && selectedSignups.length < signups.length;

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(e.target.checked);
  };

  const handleStatusChange = (signupId: string, e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    if (newStatus) {
      onStatusUpdate(signupId, newStatus);
    }
  };

  const getStatusClass = (status: string) => {
    const statusClasses = {
      'pending': 'status-pending',
      'approved': 'status-approved',
      'rejected': 'status-rejected',
      'invited': 'status-invited'
    };
    return statusClasses[status as keyof typeof statusClasses] || 'status-pending';
  };

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>
              <input 
                type="checkbox" 
                checked={allSelected}
                ref={(input) => {
                  if (input) input.indeterminate = someSelected;
                }}
                onChange={handleSelectAllChange}
              />
            </th>
            <th>Name</th>
            <th>Email</th>
            <th>Platform</th>
            <th>Experience</th>
            <th>Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {signups.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center text-gray-600">
                No signups found
              </td>
            </tr>
          ) : (
            signups.map(signup => (
              <tr key={signup.id} className={selectedSignups.includes(signup.id) ? 'selected' : ''}>
                <td>
                  <input 
                    type="checkbox" 
                    checked={selectedSignups.includes(signup.id)}
                    onChange={(e) => onSelectionChange(signup.id, e.target.checked)}
                    className="signup-checkbox"
                  />
                </td>
                <td>{signup.name || 'N/A'}</td>
                <td>{signup.email}</td>
                <td>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    background: signup.platform === 'ios' ? 'rgba(0, 122, 255, 0.1)' : signup.platform === 'android' ? 'rgba(52, 211, 153, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                    color: signup.platform === 'ios' ? '#007AFF' : signup.platform === 'android' ? '#10B981' : '#6B7280'
                  }}>
                    {signup.platform || 'N/A'}
                  </span>
                </td>
                <td>{signup.experienceYears || 'N/A'} years</td>
                <td>
                  {signup.timestamp ? new Date(signup.timestamp).toLocaleDateString() : 'N/A'}
                </td>
                <td>
                  <span className={`status-badge ${getStatusClass(signup.status)}`}>
                    {signup.status}
                  </span>
                </td>
                <td>
                  <select 
                    onChange={(e) => handleStatusChange(signup.id, e)}
                    defaultValue=""
                    className="form-select"
                  >
                    <option value="">Change Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="invited">Invited</option>
                  </select>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SignupsTable;
